import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { ImpactStyle } from "@capacitor/haptics";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Html5Qrcode, type Html5QrcodeResult } from "html5-qrcode";
import { closeOutline } from "ionicons/icons";
import { toast } from "sonner";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { logger } from "@/logger";

interface WebQRScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScanSuccess: (data: string) => void;
}

const PERMISSION_CACHE_KEY = "qr-scanner-camera-permission";

export function WebQRScanner({ isOpen, onClose, onScanSuccess }: WebQRScannerProps) {
  const { t } = useTranslation();
  const preferredHaptics = usePreferredHaptics();
  const [isScanning, setIsScanning] = useState(false);
  const [permissionState, setPermissionState] = useState<
    "prompt" | "granted" | "denied" | "checking"
  >("checking");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScanRef = useRef<string>();
  const isCleaningUpRef = useRef(false);
  const isInitializingRef = useRef(false);
  const [isFullyInitialized, setIsFullyInitialized] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeout
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }

      const stopScanner = async () => {
        if (scannerRef.current && !isCleaningUpRef.current) {
          isCleaningUpRef.current = true;
          try {
            const scanner = scannerRef.current;
            const state = scanner.getState();
            if (state === 2) {
              // 2 = SCANNING
              await scanner.stop();
            }
          } catch (error) {
            // Ignore errors on unmount - scanner might already be stopped
          } finally {
            scannerRef.current = null;
            isCleaningUpRef.current = false;
          }
        }
      };
      stopScanner();
    };
  }, []);

  // Check camera permissions when modal opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Reset closing state when modal opens
    setIsClosing(false);
    setIsFullyInitialized(false);

    // Check cached permission first
    const cachedPermission = localStorage.getItem(PERMISSION_CACHE_KEY);
    if (cachedPermission === "granted") {
      setPermissionState("granted");
      return;
    }

    const checkPermissions = async () => {
      try {
        // Check if we already have permission
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: "camera" as PermissionName });
          setPermissionState(result.state as "prompt" | "granted" | "denied");

          // Listen for permission changes
          result.addEventListener("change", () => {
            setPermissionState(result.state as "prompt" | "granted" | "denied");
            // Update cache if granted
            if (result.state === "granted") {
              localStorage.setItem(PERMISSION_CACHE_KEY, "granted");
            }
          });
        } else {
          // If permissions API is not available, assume we need to prompt
          setPermissionState("prompt");
        }
      } catch (error) {
        logger.error("Failed to check camera permissions", error);
        // Fallback to prompt state
        setPermissionState("prompt");
      }
    };

    checkPermissions();
  }, [isOpen]);

  // Start scanner when we have permission and container is ready
  useEffect(() => {
    if (!isOpen || !containerRef.current || permissionState !== "granted") {
      return;
    }

    const scannerId = "qr-reader";
    let scanner: Html5Qrcode;

    const startScanner = async () => {
      // Prevent multiple simultaneous initializations
      if (isInitializingRef.current) {
        return;
      }

      isInitializingRef.current = true;

      // Set initialization timeout
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }

      initTimeoutRef.current = setTimeout(() => {
        if (isInitializingRef.current) {
          logger.warn("Scanner initialization timeout");
          isInitializingRef.current = false;
          setIsFullyInitialized(false);
          if (isClosing) {
            onClose();
          }
        }
      }, 5000); // 5 second timeout

      try {
        // Stop any existing scanner first
        await stopScannerSafely();

        // Clear any existing content in the container
        if (containerRef.current) {
          const container = document.getElementById(scannerId);
          if (container) {
            container.innerHTML = "";
          }
        }

        scanner = new Html5Qrcode(scannerId);
        scannerRef.current = scanner;

        const qrCodeSuccessCallback = (decodedText: string, decodedResult: Html5QrcodeResult) => {
          logger.info("QR Code detected", { decodedText, decodedResult });

          // Prevent duplicate processing of the same code
          if (lastScanRef.current === decodedText) {
            return;
          }

          lastScanRef.current = decodedText;

          // Haptic feedback on successful scan
          preferredHaptics.impact(ImpactStyle.Medium);

          // Call the success handler
          onScanSuccess(decodedText);

          // Clear last scan after a delay to allow re-scanning the same code later
          setTimeout(() => {
            lastScanRef.current = undefined;
          }, 2000);
        };

        const qrCodeErrorCallback = (errorMessage: string) => {
          // Ignore "No QR code found" errors as they're expected during scanning
          if (!errorMessage.includes("NotFoundException")) {
            logger.debug("QR scanning error", { errorMessage });
          }
        };

        // Start scanning
        await scanner.start(
          { facingMode: "environment" }, // Use back camera
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          qrCodeSuccessCallback,
          qrCodeErrorCallback,
        );

        setIsScanning(true);
        isInitializingRef.current = false;
        setIsFullyInitialized(true);

        // Clear timeout on successful init
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
          initTimeoutRef.current = null;
        }

        // Check if user tried to close during init
        if (isClosing) {
          await stopScannerSafely();
          onClose();
        }
      } catch (error) {
        logger.error("Failed to start QR scanner", error);

        let errorMessage = t("qr-scanner:errors.scan-failed");
        if (error instanceof Error) {
          if (error.message.includes("NotAllowedError")) {
            errorMessage = t("qr-scanner:errors.camera-permission");
          } else if (error.message.includes("NotFoundError")) {
            errorMessage = t("qr-scanner:errors.camera-not-found");
          }
        }

        toast.error(errorMessage);

        // Clean up on error
        isInitializingRef.current = false;
        setIsFullyInitialized(false);
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
          initTimeoutRef.current = null;
        }

        // Always close on error
        onClose();
      }
    };

    startScanner();

    return () => {
      const cleanup = async () => {
        if (scanner && !isCleaningUpRef.current) {
          isCleaningUpRef.current = true;
          try {
            const state = scanner.getState();
            if (state === 2) {
              // 2 = SCANNING
              await scanner.stop();
              logger.debug("QR scanner stopped");
            }
          } catch (error) {
            // Only log if it's not a "scanner not running" error
            if (error instanceof Error && !error.message.includes("scanner is not running")) {
              logger.error("Failed to stop QR scanner", error);
            }
          } finally {
            setIsScanning(false);
            if (scannerRef.current === scanner) {
              scannerRef.current = null;
            }
            isCleaningUpRef.current = false;
          }
        }
      };
      cleanup();
    };
  }, [isOpen, onScanSuccess, onClose, t, preferredHaptics, permissionState]);

  const stopScannerSafely = async () => {
    if (scannerRef.current && !isCleaningUpRef.current) {
      isCleaningUpRef.current = true;
      try {
        const scanner = scannerRef.current;
        const state = scanner.getState();
        if (state === 2) {
          // 2 = SCANNING
          await scanner.stop();
        }
        setIsScanning(false);
        scannerRef.current = null;
      } catch (error) {
        if (error instanceof Error && !error.message.includes("scanner is not running")) {
          logger.error("Failed to stop scanner on close", error);
        }
      } finally {
        isCleaningUpRef.current = false;
      }
    }
  };

  const handleClose = async () => {
    // If we're in the middle of initialization, wait for it to complete or fail
    if (isInitializingRef.current && !isFullyInitialized) {
      setIsClosing(true);
      return;
    }

    // Otherwise, close immediately
    await stopScannerSafely();
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("qr-scanner:title")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="flex h-full flex-col items-center justify-center">
          {permissionState === "checking" && (
            <div className="text-center">
              <p className="text-muted-foreground text-sm">{t("qr-scanner:checking-permission")}</p>
            </div>
          )}

          {permissionState === "prompt" && (
            <div className="text-center">
              <p className="mb-4 text-sm">{t("qr-scanner:permission-prompt")}</p>
              <IonButton
                className="bg-primary"
                onClick={async () => {
                  setPermissionState("checking");
                  // This will trigger the browser's permission prompt
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stream.getTracks().forEach((track) => track.stop());
                    setPermissionState("granted");
                    // Cache the permission
                    localStorage.setItem(PERMISSION_CACHE_KEY, "granted");
                  } catch (error) {
                    logger.error("Camera permission denied", error);
                    setPermissionState("denied");
                    // Clear cache on denial
                    localStorage.removeItem(PERMISSION_CACHE_KEY);
                  }
                }}
              >
                {t("qr-scanner:request-permission")}
              </IonButton>
            </div>
          )}

          {permissionState === "denied" && (
            <div className="text-center">
              <p className="mb-4 text-red-500">{t("qr-scanner:errors.camera-permission")}</p>
              <p className="text-muted-foreground text-sm">
                {t("qr-scanner:permission-denied-help")}
              </p>
            </div>
          )}

          {permissionState === "granted" && (
            <>
              <div className="mb-4 text-center">
                <p className="text-muted-foreground text-sm">{t("qr-scanner:description")}</p>
              </div>
              <div
                ref={containerRef}
                id="qr-reader"
                className="mx-auto w-full max-w-md"
                style={{ minHeight: "300px" }}
              />
              {!isScanning && (
                <div className="mt-4 text-center">
                  <p className="text-muted-foreground text-sm">{t("qr-scanner:loading")}</p>
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
}
